import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { useSiteSettings } from '../SiteSettingsContext';
import {
  Stethoscope, ChevronRight, Menu, X, ArrowRight, Heart, Globe,
  Users, MapPin, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const LandingPage = () => {
  const { user, loginWithGoogle } = useAuth();
  const { logoUrl, orgName, heroImageUrl, heroImage2Url, tripsBgUrl, statsBgUrl } = useSiteSettings();
  const location = useLocation();
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Scroll to hash anchor (e.g., /#trips)
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [location.hash, trips]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tripsRes, statsRes] = await Promise.all([
          api.get('/trips?status=registration_open'),
          api.get('/stats')
        ]);
        setTrips(tripsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setDataLoaded(true);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center flex-shrink-0">
                {logoUrl ? (
                  <img src={logoUrl} alt={orgName} className="h-10 sm:h-14 max-w-[200px] sm:max-w-none w-auto object-contain" />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
                    <Stethoscope className="w-6 h-6 text-white" />
                  </div>
                )}
              </div>
            </div>
            
            {/* Desktop nav */}
            <div className="hidden sm:flex items-center gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button data-testid="go-to-dashboard-btn" className="rounded-full bg-rojo hover:bg-rojo-dark">
                    Go to Dashboard
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button data-testid="login-btn" variant="ghost" className="text-slate-600 hover:text-primary">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile hamburger */}
            <button 
              className="sm:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-600" />
              ) : (
                <Menu className="w-6 h-6 text-slate-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-white border-t border-slate-100 shadow-lg">
            <div className="px-4 py-3 space-y-2">
              <a
                href="#get-involved"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 font-medium"
              >
                Get Involved
              </a>
              <a
                href="#upcoming-trips"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 font-medium"
              >
                Upcoming Trips
              </a>
              <Link
                to="/info"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 font-medium"
              >
                About Us
              </Link>
              <div className="pt-2 border-t border-slate-100">
                {user ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full"
                  >
                    <Button className="w-full rounded-full bg-rojo hover:bg-rojo-dark">
                      Go to Dashboard
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full"
                  >
                    <Button className="w-full rounded-full bg-rojo hover:bg-rojo-dark">
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 to-amber-50" />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23306DAA' fill-opacity='0.06'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full shadow-sm">
                <Heart className="w-4 h-4 text-rojo" />
                <span className="text-sm font-medium text-slate-600">Medical Missions Since 1988</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-slate-800 leading-tight tracking-tight">
                Changing Lives Through{' '}
                <span className="text-primary">Surgical Care</span>
              </h1>

              {/* Hero image — visible on tablet portrait (below lg), hidden on desktop (shown in right column) */}
              <div className="relative block lg:hidden rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={heroImageUrl || ""}
                  alt="Guatemala Mission"
                  className="w-full aspect-[16/7] object-cover"
                  style={{ visibility: heroImageUrl ? 'visible' : 'hidden' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div className="absolute bottom-3 left-4 flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-3 py-1.5">
                  <Globe className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs font-semibold text-slate-700">Antigua, Guatemala · October Missions</span>
                </div>
              </div>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                Join our volunteer medical teams providing essential surgical procedures 
                to patients in Guatemala who cannot afford the care they desperately need.
              </p>

              <div className="flex flex-wrap gap-4">
                {user ? (
                  <Link to="/dashboard">
                    <Button 
                      data-testid="hero-dashboard-btn"
                      size="lg" 
                      className="rounded-full bg-rojo hover:bg-rojo-dark px-8"
                    >
                      View Your Dashboard
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <div className="flex flex-col gap-3 w-full max-w-sm">
                    <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Get Involved</p>
                    <Link to="/info">
                      <Button 
                        data-testid="hero-info-btn"
                        size="lg"
                        variant="outline"
                        className="w-full rounded-full border-slate-300 text-slate-700 hover:border-primary hover:text-primary px-8 justify-start"
                      >
                        <Globe className="w-4 h-4 mr-3 text-primary" />
                        Request Information
                      </Button>
                    </Link>
                    <Link to="/signup">
                      <Button
                        data-testid="hero-signup-btn"
                        size="lg"
                        variant="outline"
                        className="w-full rounded-full border-slate-300 text-slate-700 hover:border-primary hover:text-primary px-8 justify-start"
                      >
                        <Users className="w-4 h-4 mr-3 text-primary" />
                        First Time Sign Up
                      </Button>
                    </Link>
                    <Link to="/returning">
                      <Button
                        data-testid="hero-returning-btn"
                        size="lg"
                        className="w-full rounded-full bg-rojo hover:bg-rojo-dark px-8 justify-start"
                      >
                        <ArrowRight className="w-4 h-4 mr-3" />
                        Returning Participant
                      </Button>
                    </Link>
                  </div>
                )}
              </div>

              {/* Quick Stats */}
              <div className="relative rounded-2xl overflow-hidden mt-4">
                {statsBgUrl && (
                  <img 
                    src={statsBgUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                <div className={`absolute inset-0 ${statsBgUrl ? 'bg-black/40' : 'bg-slate-800'}`} />
                <div className="relative flex flex-wrap gap-8 px-8 py-6">
                  <div>
                    <p className="text-3xl font-heading font-bold text-secondary">3000+</p>
                    <p className="text-sm text-white/80">Surgeries Performed</p>
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold text-secondary">35+</p>
                    <p className="text-sm text-white/80">Years of Service</p>
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold text-secondary">140+</p>
                    <p className="text-sm text-white/80">Procedures Per Mission</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop-only right column image */}
            <div className="relative hidden lg:block">
              <div className="absolute -top-8 -right-8 w-72 h-72 bg-secondary/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-8 -left-8 w-72 h-72 bg-primary/20 rounded-full blur-3xl" />
              <img 
                src={heroImageUrl || ""}
                alt="Guatemala Landscape"
                className="relative rounded-2xl shadow-2xl w-full aspect-[4/3] object-cover"
                style={{ visibility: heroImageUrl ? 'visible' : 'hidden' }}
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Globe className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Antigua, Guatemala</p>
                    <p className="text-sm text-slate-500">October Missions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Hero Image Section */}
      <section className="py-0 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-0 items-stretch rounded-2xl overflow-hidden shadow-xl">
            {/* Image — takes 3 of 5 columns (~60% width) */}
            <div className="lg:col-span-3 h-72 lg:h-96 relative">
              <img
                src={heroImage2Url}
                alt="Mission in Action"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/60 lg:to-slate-900/50" />
            </div>
            {/* Text panel — takes 2 of 5 columns */}
            <div className="lg:col-span-2 bg-slate-900 flex flex-col justify-center px-8 py-10">
              <div className="w-10 h-1 bg-secondary rounded-full mb-5" />
              <h2 className="text-2xl lg:text-3xl font-heading font-bold text-white leading-snug mb-4">
                Making a Real Difference
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Our volunteer surgical teams bring world-class care directly to patients in Antigua. 
                Every October, we perform 140+ procedures — changing lives that would otherwise go untreated.
              </p>
              <Link to="/info">
                <Button className="rounded-full bg-rojo hover:bg-rojo-dark w-fit text-sm" size="sm">
                  Learn About the Mission
                  <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Trips Section */}
      {!dataLoaded && (
        <section className="py-16 bg-white">
          <div className="flex justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </section>
      )}

      {dataLoaded && trips.length > 0 && (
        <section id="trips" className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-heading font-bold text-slate-800 mb-4">
                Upcoming Mission Trips
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Registration is now open for our upcoming surgical missions. 
                Join us in making a difference.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trips.map((trip) => (
                <Card 
                  key={trip.trip_id} 
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
                  data-testid={`trip-card-${trip.trip_id}`}
                >
                  {/* Image — aspect-ratio ensures consistent 2/3 feel on all screen sizes */}
                  <div className="relative overflow-hidden aspect-[3/2]">
                    <img 
                      src={tripsBgUrl}
                      alt="Medical Team"
                      className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <p className="text-xs font-medium opacity-80 uppercase tracking-wider">October 2026</p>
                      <p className="text-xl font-heading font-bold leading-tight">{trip.name}</p>
                    </div>
                  </div>
                  {/* Content below */}
                  <CardContent className="p-4">
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="text-sm">{trip.start_date} – {trip.end_date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="text-sm">{trip.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Users className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="text-sm">
                          {trip.participant_count || 0} / {trip.max_participants} participants
                        </span>
                      </div>
                    </div>
                    {user ? (
                      <Link to={`/trips/${trip.trip_id}`}>
                        <Button 
                          data-testid={`view-trip-${trip.trip_id}`}
                          className="w-full rounded-full bg-rojo hover:bg-rojo-dark"
                        >
                          View Details
                        </Button>
                      </Link>
                    ) : (
                      <Link to="/returning">
                        <Button
                          data-testid={`join-trip-${trip.trip_id}`}
                          className="w-full rounded-full bg-rojo hover:bg-rojo-dark"
                        >
                          Request to Join
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Info Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-0 shadow-sm rounded-xl p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-secondary/15 rounded-xl flex items-center justify-center mb-4">
                <Stethoscope className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-800 mb-2">Medical Excellence</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Our teams include experienced surgeons, anesthesiologists, nurses, and support staff 
                providing high-quality surgical care.
              </p>
            </Card>

            <Card className="bg-white border-0 shadow-sm rounded-xl p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-rojo/10 rounded-xl flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-rojo" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-800 mb-2">100% Volunteer</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                All team members volunteer their time and cover their own expenses. 
                100% of donations go directly to patient care.
              </p>
            </Card>

            <Card className="bg-white border-0 shadow-sm rounded-xl p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-800 mb-2">Local Partnership</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                We partner with Hermano Pedro Hospital and Common Hope to ensure 
                sustainable, community-centered care.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-secondary/20 bg-amber-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            {/* Logos */}
            <div className="space-y-4">
              <Link to="/" className="inline-block">
                {logoUrl ? (
                  <img src={logoUrl} alt={orgName} className="h-12 w-auto object-contain" />
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                      <Stethoscope className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-heading font-bold text-xl text-slate-800">{orgName}</span>
                  </div>
                )}
              </Link>
              <a href="https://www.commonhope.org" target="_blank" rel="noopener noreferrer" className="inline-block">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/site-settings/image/partner_logo`}
                  alt="Common Hope | Familias de Esperanza"
                  className="h-10 w-auto object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </a>
            </div>

            {/* Quick links */}
            <div className="space-y-3">
              <h4 className="font-heading font-semibold text-sm text-slate-700 uppercase tracking-wide">Get Involved</h4>
              <div className="flex flex-col gap-2">
                <Link to="/info" className="text-sm text-slate-500 hover:text-primary transition-colors">
                  Mission Information
                </Link>
                <Link to="/signup" className="text-sm text-slate-500 hover:text-primary transition-colors">
                  First Time Sign Up
                </Link>
                <Link to="/returning" className="text-sm text-slate-500 hover:text-primary transition-colors">
                  Returning Participant
                </Link>
              </div>
            </div>

            {/* Partners & donate */}
            <div className="space-y-3">
              <h4 className="font-heading font-semibold text-sm text-slate-700 uppercase tracking-wide">Partners</h4>
              <div className="flex flex-col gap-2">
                <a href="https://give.commonhope.org/campaign/guatemala-surgery/c222592" 
                   target="_blank" rel="noopener noreferrer"
                   className="text-sm text-rojo font-medium hover:text-rojo-dark transition-colors">
                  Donate to the Mission
                </a>
                <a href="https://www.commonhope.org" 
                   target="_blank" rel="noopener noreferrer"
                   className="text-sm text-slate-500 hover:text-primary transition-colors">
                  Common Hope
                </a>
                <a href="https://www.hermanopedrofoundationusa.org" 
                   target="_blank" rel="noopener noreferrer"
                   className="text-sm text-slate-500 hover:text-primary transition-colors">
                  Hermano Pedro Foundation
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-10 pt-6 border-t border-secondary/15 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} {orgName}. All rights reserved.
            </p>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-primary" />
              <div className="w-2 h-2 rounded-full bg-secondary" />
              <div className="w-2 h-2 rounded-full bg-rojo" />
              <span className="text-xs text-slate-400 ml-1.5">Colors of Antigua</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
